import type React from "react";
import { measurementSources, takeoffSources } from "@Actions/estimation";
import { t, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import type { TakeoffData, TakeoffListItemAttached, MeasurementUnit } from "@ServerTypes/takeoffs/takeoffs.schema";
import type { MeasurementItem } from "@ServerTypes/measurements/measurements.schema";
import type { Formatter as SlickFormatter } from "slickgrid-react";

/**
 * Row shape a SlickGrid formatter receives for the takeoff list column set.
 * The grid receives full takeoff items; both the full TakeoffData and the
 * lighter "attached" variant are supported.
 */
type TakeoffGridRow = TakeoffData | TakeoffListItemAttached;

/**
 * Row shape for the measurements grid. Extends the Zod `MeasurementItem` with
 * the nested `unit` object the grid hydrates at runtime (sourced from
 * `MeasurementUnits` aggregation in the backend).
 */
type MeasurementGridRow = MeasurementItem & {
  // `id` is picked out of `MeasurementUnit` and made optional rather than re-declared as
  // `{ id?: string }` — re-declaring it dropped the field's `ObjectIdString` brand.
  unit?: Pick<MeasurementUnit, "name" | "type" | "factor"> & Partial<Pick<MeasurementUnit, "id">>;
};

/**
 * SlickGrid formatter signature used by these column definitions.
 * Aliases slickgrid-react's official Formatter so callers can pass it directly
 * without contravariance issues on the column-def parameter.
 */
type GridFormatter<TRow> = SlickFormatter<TRow>;

type GridValueGetter<TRow> = (params: TRow) => string | number | undefined;

type GridHeaderRenderer = () => string | HTMLElement;

type GridEditorTemplate<TRow> = (args: {
  value: string | number | null;
  updatedColumn: Column;
  args: TRow;
}) => HTMLElement | React.ReactNode;

interface TakeoffListColumnsParams {
  source: string;
  nameTemplate?: GridFormatter<TakeoffGridRow>;
  quantityTemplate?: GridFormatter<TakeoffGridRow>;
  unitTemplate?: GridFormatter<TakeoffGridRow>;
  createdByTemplate?: GridFormatter<TakeoffGridRow>;
  dateTemplate?: GridFormatter<TakeoffGridRow>;
  executeQueryTemplate?: GridFormatter<TakeoffGridRow>;
  estimateVersionId?: string | number;
}

interface Column {
  field?: string;
  id?: string;
  /** Column header display name - must be wrapped in t() for i18n */
  name?: TranslatedString;
  textAlign?: string;
  filterType?: string;
  formatter?: GridFormatter<TakeoffGridRow> | GridFormatter<MeasurementGridRow>;
  minWidth?: number;
  maxWidth?: number;
  _valueGetter?: GridValueGetter<TakeoffGridRow> | GridValueGetter<MeasurementGridRow>;
  fieldKey?: string;
  _customHeaderRenderer?: GridHeaderRenderer;
  disableMenuOptions?: boolean;
  disableGrouping?: boolean;
  editorTemplate?: GridEditorTemplate<TakeoffGridRow> | GridEditorTemplate<MeasurementGridRow>;
  disableFiltering?: boolean;
  disableSorting?: boolean;
}

export const getTakeoffListColums = ({
  source,
  nameTemplate,
  quantityTemplate,
  unitTemplate,
  createdByTemplate,
  dateTemplate,
  executeQueryTemplate,
  estimateVersionId,
}: TakeoffListColumnsParams): Column[] => {
  if (source === takeoffSources.VIEWER) {
    return [
      {
        field: "name",
        id: "name",
        name: t("Takeoff Name"),
        textAlign: "Left",
        filterType: "Text",
        formatter: nameTemplate,
        minWidth: 200,
      },
      {
        field: "quantity",
        id: "quantity",
        name: t("Quantity"),
        textAlign: "Left",
        formatter: quantityTemplate,
        minWidth: 200,
      },

      {
        field: "measurementUnit.selectedSymbol.name",
        name: t("Units"),
        id: "unitSymbol",
        textAlign: "Left",
        filterType: "Text",
        formatter: unitTemplate,
        _valueGetter: (params: TakeoffGridRow) => params.measurementUnit.selectedSymbol.name,
        minWidth: 200,
      },
    ];
  } else if (source === takeoffSources.ESTIMATE) {
    return [
      {
        field: "name",
        name: t("Takeoff Name"),
        id: "name",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 100,
        maxWidth: 250,
      },
      {
        field: "description",
        name: t("Description"),
        id: "description",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 100,
        maxWidth: 200,
      },
      {
        field: "quantity",
        id: "quantity",
        name: t("Quantity"),
        textAlign: "Left",
        formatter: quantityTemplate,
        maxWidth: 150,
      },
      {
        field: "measurementUnit.selectedSymbol.name",
        name: t("Units"),
        id: "unitSymbol",
        textAlign: "Left",
        filterType: "Text",
        formatter: unitTemplate,
        _valueGetter: (params: TakeoffGridRow) => params.measurementUnit.selectedSymbol.name,
      },
      {
        field: "createdBy.fullName",
        id: "createdBy",
        name: t("Created by"),
        textAlign: "Left",
        // filterType: "Text",
        formatter: createdByTemplate,
        _valueGetter: (params: TakeoffGridRow) => ("createdBy" in params ? params.createdBy.fullName : undefined),
      },
      {
        field: "createdDate",
        id: "createdDate",
        name: t("Created on"),
        textAlign: "Left",
        formatter: dateTemplate,
      },

      ...(estimateVersionId
        ? []
        : [
            {
              id: "executeQuery",
              name: untranslated(""),
              textAlign: "Left",
              formatter: executeQueryTemplate,
              maxWidth: 100,
              minWidth: 100,
            },
          ]),
    ];
  }
  return [];
};

const measurementColumnsFieldKeyMap = {
  HIGHLIGHT: "highlight",
  SHOWHIDE: "showHide",
  NAME: "name",
  TYPE: "type",
  MEASUREMENT: "measurement",
  UNIT: "unit",
  TAKEOFFS: "takeoffs",
  FILE: "file",
  SCALE: "scale",
  MULTIPLIER: "multiplier",
  NESTED_MEASUREMENTS: "nestedMeasurements",
  MEASUREDVALUE: "measuredValue",
  NATURE: "nature",
  TAGS: "tags",
} as const;

interface MeasurementsListColumnsParams {
  source: string;
  nameTemplate?: GridFormatter<MeasurementGridRow>;
  hightlightMeasurementTemplate?: GridFormatter<MeasurementGridRow>;
  tagsTemplate?: GridFormatter<MeasurementGridRow>;
  eyeIconTemplate?: GridFormatter<MeasurementGridRow>;
  eyeIconHeaderTemplate?: GridHeaderRenderer;
  isFilterAllowed?: boolean;
  measurementTemplate?: GridFormatter<MeasurementGridRow>;
  measurementTypeTemplate?: GridFormatter<MeasurementGridRow>;
  takeOffsTemplate?: GridFormatter<MeasurementGridRow>;
  nestedMeasurementTemplate?: GridFormatter<MeasurementGridRow>;
  measuredValueTemplate?: GridFormatter<MeasurementGridRow>;
  unitsTemplate?: GridFormatter<MeasurementGridRow>;
  nameEditTemplate?: GridEditorTemplate<MeasurementGridRow>;
  multiplierEditTemplate?: GridEditorTemplate<MeasurementGridRow>;
}

export const getMeasurementsListColumns = ({
  source,
  nameTemplate,
  hightlightMeasurementTemplate,
  tagsTemplate,
  eyeIconTemplate,
  eyeIconHeaderTemplate,
  isFilterAllowed,
  measurementTemplate,
  measurementTypeTemplate,
  takeOffsTemplate,
  nestedMeasurementTemplate,
  measuredValueTemplate,
  unitsTemplate,
  nameEditTemplate,
  multiplierEditTemplate,
}: MeasurementsListColumnsParams): Column[] => {
  const columnsBySourceMap: { [K in keyof typeof measurementSources as (typeof measurementSources)[K]]: string[] } = {
    [measurementSources.MEASUREMENTS]: [
      measurementColumnsFieldKeyMap.SHOWHIDE,
      measurementColumnsFieldKeyMap.NAME,
      measurementColumnsFieldKeyMap.TYPE,
      measurementColumnsFieldKeyMap.MEASUREMENT,
      measurementColumnsFieldKeyMap.UNIT,
      measurementColumnsFieldKeyMap.TAKEOFFS,
      measurementColumnsFieldKeyMap.SCALE,
      measurementColumnsFieldKeyMap.MULTIPLIER,
      measurementColumnsFieldKeyMap.NESTED_MEASUREMENTS,
      measurementColumnsFieldKeyMap.MEASUREDVALUE,
      measurementColumnsFieldKeyMap.NATURE,
      measurementColumnsFieldKeyMap.TAGS,
    ],
    [measurementSources.ESTIMATE]: Object.values(measurementColumnsFieldKeyMap).filter(
      (value) => value != measurementColumnsFieldKeyMap.SHOWHIDE,
    ),
    [measurementSources.TAKEOFFS]: [
      measurementColumnsFieldKeyMap.NAME,
      measurementColumnsFieldKeyMap.MEASUREMENT,
      measurementColumnsFieldKeyMap.UNIT,
      measurementColumnsFieldKeyMap.FILE,
      measurementColumnsFieldKeyMap.MULTIPLIER,
      measurementColumnsFieldKeyMap.MEASUREDVALUE,
    ],
    [measurementSources.TAKEOFF_SIDEBAR]: [
      measurementColumnsFieldKeyMap.NAME,
      measurementColumnsFieldKeyMap.MEASUREMENT,
      measurementColumnsFieldKeyMap.UNIT,
      measurementColumnsFieldKeyMap.FILE,
      measurementColumnsFieldKeyMap.SCALE,
      measurementColumnsFieldKeyMap.MULTIPLIER,
      measurementColumnsFieldKeyMap.MEASUREDVALUE,
      measurementColumnsFieldKeyMap.TAGS,
    ],
    [measurementSources.REFERENCE_MEASUREMENT]: [
      measurementColumnsFieldKeyMap.NAME,
      measurementColumnsFieldKeyMap.TYPE,
      measurementColumnsFieldKeyMap.MEASUREMENT,
      measurementColumnsFieldKeyMap.UNIT,
      measurementColumnsFieldKeyMap.TAKEOFFS,
    ],
  };

  const columns: Column[] = [
    {
      fieldKey: measurementColumnsFieldKeyMap.HIGHLIGHT,
      id: "highlight",
      textAlign: "Left",
      formatter: hightlightMeasurementTemplate,
      minWidth: 50,
      maxWidth: 50,
      disableMenuOptions: true,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.SHOWHIDE,
      field: "eyeIcon",
      id: "eyeIcon",
      formatter: eyeIconTemplate,
      _customHeaderRenderer: eyeIconHeaderTemplate,
      disableMenuOptions: true,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.NAME,
      field: "name",
      name: t("Name"),
      id: "name",
      textAlign: "Left",
      filterType: isFilterAllowed ? "Text" : undefined,
      minWidth: 170,
      formatter: [measurementSources.MEASUREMENTS, measurementSources.ESTIMATE].includes(source)
        ? nameTemplate
        : undefined,
      editorTemplate: nameEditTemplate,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.TYPE,
      field: "unit.type.name",
      name: t("Type"),
      id: "type",
      textAlign: "Left",
      formatter: measurementTypeTemplate,
      _valueGetter: (params: MeasurementGridRow) => params.unit?.type.name,
      filterType: isFilterAllowed ? "Text" : undefined,
      minWidth: 120,
      disableSorting: true,
      disableFiltering: true,
      disableGrouping: true,
      disableMenuOptions: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.MEASUREMENT,
      field: "quantity",
      id: "measurement",
      name: t("Measurement"),
      textAlign: "Left",
      formatter: measurementTemplate,
      minWidth: 130,
      disableSorting: true,
      disableFiltering: true,
      disableGrouping: true,
      disableMenuOptions: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.UNIT,
      field: "unit.name",
      name: t("Units"),
      id: "units",
      _valueGetter: (params: MeasurementGridRow) => params.unit?.name,
      textAlign: "Left",
      formatter: unitsTemplate,
      filterType: isFilterAllowed ? "Text" : undefined,
      minWidth: 130,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.TAKEOFFS,
      field: "takeOffs",
      id: "takeOffs",
      name: t("Takeoffs"),
      textAlign: "Left",
      formatter: takeOffsTemplate,
      minWidth: 180,
      disableMenuOptions: true,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.FILE,
      field: "fileName",
      id: "fileName",
      name: t("File"),
      textAlign: "Left",
      minWidth: 180,
      disableFiltering: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.SCALE,
      field: "scale",
      name: t("Scale"),
      id: "scale",
      textAlign: "Left",
      filterType: isFilterAllowed ? "Text" : undefined,
      minWidth: 120,
      disableGrouping: true,
      disableSorting: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.MULTIPLIER,
      field: "multiplier",
      name: t("Multiplier"),
      textAlign: "Left",
      id: "multiplier",
      filterType: isFilterAllowed ? "Number" : undefined,
      minWidth: 120,
      editorTemplate: multiplierEditTemplate,
      disableGrouping: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.NESTED_MEASUREMENTS,
      id: "nestedMeasurements",
      name: t("Nested Measurements"),
      textAlign: "Left",
      formatter: nestedMeasurementTemplate,
      minWidth: 250,
      disableMenuOptions: true,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.MEASUREDVALUE,
      field: "quantity",
      id: "measuredValue",
      name: t("Measured Value"),
      textAlign: "Left",
      formatter: measuredValueTemplate,
      minWidth: 130,
      disableSorting: true,
      disableFiltering: true,
      disableGrouping: true,
      disableMenuOptions: true,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.NATURE,
      field: "nature",
      name: t("Nature"),
      id: "nature",
      textAlign: "Left",
      filterType: isFilterAllowed ? "Text" : undefined,
      minWidth: 120,
      maxWidth: 120,
    },
    {
      fieldKey: measurementColumnsFieldKeyMap.TAGS,
      field: "tags",
      id: "tags",
      name: t("Tags"),
      textAlign: "Left",
      formatter: tagsTemplate,
      minWidth: 150,
      disableMenuOptions: true,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
    },
  ];

  const allowedFields =
    columnsBySourceMap[source as (typeof measurementSources)[keyof typeof measurementSources]] ?? [];
  return columns.filter((column: Column) => allowedFields.includes(column.fieldKey!));
};
